import { useAuth, useClerk } from '@clerk/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BadgeCheck, LoaderCircle, MessageSquarePlus, Star } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { apiRequest, formatDate } from '../lib/api'
import type { Product, Review } from '../types'
import { Modal } from './Modal'

interface ReviewsResponse {
  data: Review[]
  meta: { rating: number; total: number }
}

export function Reviews({ product }: { product: Product }) {
  const [modalOpen, setModalOpen] = useState(false)
  const [rating, setRating] = useState(5)
  const [body, setBody] = useState('')
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const { openSignIn } = useClerk()
  const queryClient = useQueryClient()

  const reviews = useQuery({
    queryKey: ['reviews', product.slug],
    queryFn: () => apiRequest<ReviewsResponse>(`/api/products/${product.slug}/reviews?limit=12`),
  })

  const submitReview = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('Sign in to publish your review.')
      return apiRequest<{ data: { saved: boolean } }>(`/api/products/${product.id}/reviews`, {
        method: 'POST',
        token,
        json: { rating, body: body.trim() },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reviews', product.slug] })
      setModalOpen(false)
      setBody('')
      setRating(5)
    },
  })

  const openReview = () => {
    if (!isLoaded) return
    if (!isSignedIn) {
      void openSignIn({})
      return
    }
    submitReview.reset()
    setModalOpen(true)
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    submitReview.mutate()
  }

  const data = reviews.data?.data ?? []
  const meta = reviews.data?.meta ?? { rating: product.rating, total: product.reviewsCount }

  return (
    <>
      <div className="reviews-heading">
        <div>
          <span className="eyebrow"><span>03</span> VERIFIED REVIEWS</span>
          <h2>Opinions without <em>noise.</em></h2>
          <p>Only people who bought {product.name} can publish. No invented testimonials, no shortcuts.</p>
        </div>
        <div className="rating-orb">
          <strong>{meta.total > 0 ? meta.rating.toFixed(1) : '—'}</strong>
          <div>{[1, 2, 3, 4, 5].map((value) => <Star key={value} size={13} fill={value <= Math.round(meta.rating) ? 'currentColor' : 'none'} />)}</div>
          <span>{meta.total} {meta.total === 1 ? 'review' : 'reviews'}</span>
        </div>
      </div>

      {reviews.isLoading ? (
        <div className="review-grid" aria-label="Loading reviews">
          {[1, 2, 3].map((item) => <div className="review-card review-card--skeleton" key={item} />)}
        </div>
      ) : data.length > 0 ? (
        <div className="review-grid">
          {data.map((review) => (
            <article className="review-card" key={review.id}>
              <div className="review-card__stars" aria-label={`${review.rating} out of 5 stars`}>
                {[1, 2, 3, 4, 5].map((value) => <Star key={value} size={13} fill={value <= review.rating ? 'currentColor' : 'none'} />)}
              </div>
              {review.body ? <p>“{review.body}”</p> : null}
              <footer>
                <span className="review-avatar">
                  {review.author.avatarUrl ? <img src={review.author.avatarUrl} alt="" /> : review.author.name.slice(0, 1).toUpperCase()}
                </span>
                <div><strong>{review.author.name}</strong><small><BadgeCheck size={12} /> Verified purchase</small></div>
                <time dateTime={review.createdAt}>{formatDate(review.createdAt)}</time>
              </footer>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-reviews">
          <div><Star size={22} /></div>
          <h3>The first review will be real.</h3>
          <p>When someone tries {product.name}, their experience will show up here. We would rather start at zero than fabricate trust.</p>
        </div>
      )}

      <div className="review-action">
        <button className="button button--secondary" type="button" onClick={openReview}>
          <MessageSquarePlus size={16} /> Write a review
        </button>
        <span>Requires a verified purchase</span>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={`Rate ${product.name}`} eyebrow="VERIFIED REVIEW" size="compact">
        <form className="review-form" onSubmit={onSubmit}>
          <fieldset>
            <legend>Your rating</legend>
            <div className="star-input">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setRating(value)}
                  aria-label={`${value} stars`}
                  aria-pressed={rating === value}
                >
                  <Star fill={value <= rating ? 'currentColor' : 'none'} />
                </button>
              ))}
            </div>
          </fieldset>
          <label>Comment <span className="review-form__optional">optional</span>
            <textarea value={body} onChange={(event) => setBody(event.target.value)} maxLength={800} rows={4} placeholder={`Anything you want to add about ${product.name}?`} />
          </label>
          {submitReview.error ? <div className="form-message form-message--error" role="alert">{submitReview.error.message}</div> : null}
          <button className="button button--primary button--wide" type="submit" disabled={submitReview.isPending}>
            {submitReview.isPending ? <><LoaderCircle className="spin" size={17} /> Publishing…</> : 'Publish review'}
          </button>
        </form>
      </Modal>
    </>
  )
}
